/* AKOO storefront helpers, Sept 2026. Loaded deferred from layout/theme.liquid. */
(function () {
  'use strict';

  /* 1. Cart drawer: Redo (package protection) mounts its own checkout button inside the drawer's shadow
        footer. Flag the drawer so CSS can keep the theme's native checkout button hidden while Redo is
        present, and restore it when Redo is absent (blocked, failed, or uninstalled). */
  var watchedDrawer = null;

  /* "Redo present" means Redo's widget is actually rendered (it can mount an empty host, e.g. on an unpublished
     theme or before its config loads); an empty host must not hide the theme's own checkout button. */
  function redoRendered(drawer, root) {
    var toggle = (root && root.querySelector('redo-shopify-toggle')) || drawer.querySelector('redo-shopify-toggle');
    if (!toggle) return false;
    var rect = toggle.getBoundingClientRect();
    return rect.height > 24 && rect.width > 80;
  }

  /* The drawer must be able to check out even when Redo's widget fails to mount (observed live on
     publish day: Redo's script loaded, its toggle never appeared, and its stylesheet still hid the
     theme's button). The button sits in the drawer's open shadow root where our stylesheet cannot
     reach, so force it back with inline important styles, and hand back to Redo the moment its
     widget renders. */
  function ensureNativeCheckout(drawer, root, hasRedo) {
    var btn = (root && root.querySelector('button[name="checkout"]')) || drawer.querySelector('button[name="checkout"]');
    if (!btn) return;
    var PROPS = ['display', 'visibility', 'opacity', 'pointer-events', 'height', 'max-height',
                 'max-width', 'width', 'padding', 'overflow', 'clip', 'clip-path'];
    if (hasRedo) {
      if (btn.hasAttribute('data-akoo-forced')) {
        PROPS.forEach(function (p) { btn.style.removeProperty(p); });
        btn.removeAttribute('data-akoo-forced');
      }
      return;
    }
    if (getComputedStyle(btn).display === 'none' || btn.getBoundingClientRect().height < 10) {
      var f = function (p, v) { btn.style.setProperty(p, v, 'important'); };
      f('display', 'flex'); f('visibility', 'visible'); f('opacity', '1'); f('pointer-events', 'auto');
      f('height', 'auto'); f('max-height', 'none'); f('max-width', 'none'); f('width', '100%');
      f('padding', '0.75rem 1.5rem'); f('overflow', 'visible'); f('clip', 'auto'); f('clip-path', 'none');
      btn.setAttribute('data-akoo-forced', '');
    }
  }

  function syncRedo() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    var root = drawer.shadowRoot;
    var hasRedo = redoRendered(drawer, root);
    drawer.classList.toggle('akoo-has-redo', hasRedo);
    ensureNativeCheckout(drawer, root, hasRedo);
    placeSummary(drawer, root, hasRedo);
    if (root && watchedDrawer !== drawer) {
      watchedDrawer = drawer;
      new MutationObserver(syncRedo).observe(root, { childList: true, subtree: true });
    }
  }

  /* Redo renders above the theme's footer slot, which would push the subtotal under the checkout button.
     When Redo is present, move the subtotal block into the shadow footer ahead of Redo's widget. */
  var SUMMARY_STYLE = '.akoo-cart-summary{display:grid;gap:.25rem;width:100%;margin-block-end:.875rem;font-family:inherit}' +
    '.akoo-cart-summary__row{display:flex;justify-content:space-between;align-items:baseline;font-size:var(--text-sm);letter-spacing:.06em;text-transform:uppercase}' +
    '.akoo-cart-summary__row strong{font-weight:600;letter-spacing:0;font-size:1rem}' +
    '.akoo-cart-summary__note{margin:0;font-size:var(--text-xs);color:rgb(var(--text-color) / .6)}';

  function placeSummary(drawer, root, hasRedo) {
    if (!root) return;
    var lightSummary = drawer.querySelector('.akoo-cart-summary');
    var shadowSummary = root.querySelector('.akoo-cart-summary');
    var toggle = root.querySelector('redo-shopify-toggle');
    if (hasRedo && toggle && lightSummary) {
      if (!root.querySelector('style[data-akoo]')) {
        var style = document.createElement('style');
        style.setAttribute('data-akoo', '');
        style.textContent = SUMMARY_STYLE;
        root.appendChild(style);
      }
      if (shadowSummary) shadowSummary.remove();
      toggle.parentNode.insertBefore(lightSummary, toggle);
    } else if (!hasRedo && shadowSummary && !lightSummary) {
      var form = drawer.querySelector('form.cart-drawer__footer');
      if (form) form.insertBefore(shadowSummary, form.querySelector('.button-group'));
    }
  }

  /* 2. Free-shipping bar: expose progress as a CSS custom property so the track can be drawn in CSS.
        Prestige stores threshold and total-price in cents on the element and updates total-price on cart:change. */
  function syncShippingProgress() {
    var bars = document.querySelectorAll('free-shipping-bar');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var threshold = parseFloat(bar.getAttribute('threshold')) || 0;
      var total = parseFloat(bar.getAttribute('total-price')) || 0;
      if (threshold > 0 && threshold < 1000) threshold = threshold * 100; /* before the element normalises to cents */
      var pct = threshold > 0 ? Math.max(0, Math.min(100, Math.round((total / threshold) * 100))) : 100;
      bar.style.setProperty('--akoo-fs-progress', pct + '%');
      if (!bar.__akooObserved) {
        bar.__akooObserved = true;
        new MutationObserver(syncShippingProgress).observe(bar, { attributes: true, attributeFilter: ['total-price', 'threshold'] });
      }
    }
  }

  /* 3. Product page sticky bar.
        Prestige shows the bar only after the real Add to Cart has scrolled off the top, and its button adds whatever
        size was pre-selected. Here: show it whenever the button is off-screen (above or below, footer not visible), and
        until the shopper has actually picked a size, label it "Select size" and scroll them to the size picker instead
        of adding. Single-variant products keep the plain add behaviour. */
  var stickyObserved = typeof WeakSet === 'function' ? new WeakSet() : null;
  var SELECT_LABEL = 'Select size';

  function pickerFor(formId) {
    return document.querySelector('variant-picker[form-id="' + formId + '"]');
  }

  function needsChoice(formId) {
    var form = document.forms[formId];
    var picker = pickerFor(formId);
    if (!form || !picker) return false;
    if (!picker.querySelector('input[type="radio"], select')) return false;
    if (form.dataset.akooSizeChosen === '1') return false;
    if (/[?&]variant=/.test(location.search)) return false;
    return true;
  }

  function syncStickyBar() {
    var bars = document.querySelectorAll('product-sticky-bar');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var formId = bar.getAttribute('form');
      var btn = bar.querySelector('button[type="submit"]');
      if (btn && !btn.disabled) {
        if (needsChoice(formId)) {
          if (!btn.dataset.akooOriginal) btn.dataset.akooOriginal = btn.innerHTML;
          if (btn.textContent.trim() !== SELECT_LABEL) btn.textContent = SELECT_LABEL;
        } else if (btn.dataset.akooOriginal && btn.textContent.trim() === SELECT_LABEL) {
          btn.innerHTML = btn.dataset.akooOriginal;
        }
      }
      if (stickyObserved && !stickyObserved.has(bar)) {
        stickyObserved.add(bar);
        (function (bar, formId) {
          var form = document.forms[formId];
          var footer = document.querySelector('.shopify-section--footer');
          if (!form || !('IntersectionObserver' in window)) return;
          var formVisible = true, footerVisible = false;
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (e) {
              if (e.target === form) formVisible = e.isIntersecting;
              if (e.target === footer) footerVisible = e.isIntersecting;
            });
            bar.classList.toggle('akoo-visible', !formVisible && !footerVisible);
          });
          io.observe(form);
          if (footer) io.observe(footer);
        })(bar, formId);
      }
    }
  }

  document.addEventListener('change', function (e) {
    var input = e.target;
    if (!input || !input.closest || !input.closest('variant-picker')) return;
    var formId = input.getAttribute('form') || (input.closest('variant-picker') || {}).getAttribute('form-id');
    var form = formId && document.forms[formId];
    if (form) form.dataset.akooSizeChosen = '1';
    syncStickyBar();
  }, true);

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('product-sticky-bar button[type="submit"]') : null;
    if (!btn) return;
    var formId = btn.getAttribute('form') || btn.closest('product-sticky-bar').getAttribute('form');
    if (!needsChoice(formId)) return;
    e.preventDefault();
    e.stopPropagation();
    var picker = pickerFor(formId);
    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    picker.classList.add('akoo-picker-pulse');
    setTimeout(function () { picker.classList.remove('akoo-picker-pulse'); }, 1800);
  }, true);

  /* 4. Prestige bug (present on the live theme too): emptying the cart animates the drawer's shadow footer to
        opacity 0 / translateY(30px) and leaves those inline styles in place. If products are added again while the
        drawer stays open (e.g. from the "You may also like" upsell), Prestige only fades the body back in, so the
        footer with subtotal and checkout stays invisible. Restore it whenever the drawer has items. */
  function restoreDrawerFooter() {
    var drawer = document.getElementById('cart-drawer');
    var root = drawer && drawer.shadowRoot;
    if (!root) return;
    var footer = root.querySelector('[part="footer"]');
    if (!footer) return;
    if (drawer.classList.contains('drawer--center-body')) return; /* cart is empty: footer is meant to be hidden */
    if (!drawer.querySelector('form.cart-drawer__footer')) return;
    var cs = getComputedStyle(footer);
    if (cs.display !== 'none' && (cs.opacity === '0' || /translateY\(30px\)/.test(footer.style.transform || ''))) {
      footer.style.transition = 'opacity 0.25s ease-out, transform 0.25s ease-out';
      footer.style.opacity = '1';
      footer.style.transform = 'translateY(0)';
    }
  }

  document.addEventListener('cart-drawer:refreshed', function () {
    restoreDrawerFooter();
    setTimeout(restoreDrawerFooter, 350);
  });

  function run() {
    syncRedo();
    syncShippingProgress();
    syncStickyBar();
    restoreDrawerFooter();
  }

  run();
  document.addEventListener('cart:change', run);
  document.addEventListener('cart:refresh', run);
  new MutationObserver(function () { run(); }).observe(document.body, { childList: true, subtree: true });
  /* Redo mounts and sizes its widget asynchronously after cart changes; re-check while the drawer is open. */
  setInterval(function () {
    var drawer = document.getElementById('cart-drawer');
    if (drawer && drawer.hasAttribute('open')) { syncRedo(); restoreDrawerFooter(); }
  }, 400);
})();

/* Accessibility pass (Sept 16 2026). Apps inject controls with missing names, and carousels leave
   focusable content inside aria-hidden slides. Patch what theme code can reach, re-apply as apps
   re-render, and reverse the tabindex patch when a slide rotates back into view. */
(function () {
  function pass() {
    var toastClose = document.getElementById('spb__cartToastCloseBtn');
    if (toastClose && !toastClose.getAttribute('aria-label')) toastClose.setAttribute('aria-label', 'Close');

    var pbar = document.getElementById('PBarNextFrame');
    if (pbar && !pbar.getAttribute('title')) pbar.setAttribute('title', 'Promotions');

    document.querySelectorAll('a[data-cmp-vendor]:not([aria-label])').forEach(function (a) {
      if (!a.textContent.trim()) a.setAttribute('aria-label', 'Privacy information');
    });

    document.querySelectorAll('a.slideshow__slide:not([aria-label]), .slideshow__slide a:not([aria-label])').forEach(function (a) {
      if (a.textContent.trim()) return;
      var scope = a.classList.contains('slideshow__slide') ? a : a.closest('.slideshow__slide');
      var heading = scope && scope.querySelector('h1, h2, h3, [class*="heading"]');
      a.setAttribute('aria-label', (heading && heading.textContent.trim()) || 'Featured promotion');
    });

    document.querySelectorAll('[id^="product-gallery-carousel"]:not([tabindex])').forEach(function (g) {
      g.setAttribute('tabindex', '0');
      if (!g.getAttribute('aria-label')) g.setAttribute('aria-label', 'Product images');
    });

    /* Essential Upsell renders its row as a horizontally scrolling div with no keyboard access.
       A plain div may not carry aria-label, so give it a role in the same breath. */
    document.querySelectorAll('[data-essential-upsell-element="products"]:not([tabindex])').forEach(function (r) {
      r.setAttribute('tabindex', '0');
      if (!r.getAttribute('role')) r.setAttribute('role', 'group');
      if (!r.getAttribute('aria-label')) r.setAttribute('aria-label', 'Recommended products');
    });

    /* Category tiles repeat their link text in the image alt, which screen readers read twice. */
    document.querySelectorAll('.category-tile img[alt], [class*="collection-card"] img[alt]').forEach(function (img) {
      var link = img.closest('a');
      if (!link) return;
      var label = link.textContent.replace(/\s+/g, ' ').trim();
      if (label && label.toLowerCase() === img.getAttribute('alt').trim().toLowerCase()) img.setAttribute('alt', '');
    });

    document.querySelectorAll('[data-akoo-a11y-untabbed]').forEach(function (el) {
      if (el.closest('[aria-hidden="true"]')) return;
      var prev = el.getAttribute('data-akoo-a11y-untabbed');
      if (prev) { el.setAttribute('tabindex', prev); } else { el.removeAttribute('tabindex'); }
      el.removeAttribute('data-akoo-a11y-untabbed');
    });
    document.querySelectorAll('div[role="group"][aria-hidden="true"]').forEach(function (slide) {
      slide.querySelectorAll('a, button, select, input, [tabindex="0"]').forEach(function (el) {
        if (el.getAttribute('tabindex') === '-1') return;
        el.setAttribute('data-akoo-a11y-untabbed', el.getAttribute('tabindex') || '');
        el.setAttribute('tabindex', '-1');
      });
    });
  }

  var t;
  function schedule() { clearTimeout(t); t = setTimeout(pass, 400); }
  pass();
  window.addEventListener('load', function () { pass(); setTimeout(pass, 2500); });
  new MutationObserver(schedule).observe(document.body, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden']
  });
})();
